import React, { useEffect, useRef } from 'react';
import { LuX } from 'react-icons/lu';
import { useBreakpoint } from '../../../hooks/useBreakpoint';
import './ResponsiveDialog.less';

/**
 * ResponsiveDialog — mobile bottom sheet, desktop centered dialog.
 * Focus is trapped to the dialog; Escape closes; overlay click closes
 * unless dismissable=false.
 */
function ResponsiveDialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  dismissable = true,
  labelledBy,
  size = 'md',
  className = '',
}) {
  const bp = useBreakpoint();
  const containerRef = useRef(null);
  const previousFocus = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    previousFocus.current = document.activeElement;
    // Focus the container so keyboard users start inside the dialog.
    const t = setTimeout(() => {
      const focusable = containerRef.current?.querySelector(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      (focusable || containerRef.current)?.focus?.();
    }, 0);
    function onKey(e) {
      if (e.key === 'Escape' && dismissable) { onClose?.(); }
    }
    document.addEventListener('keydown', onKey);
    return () => {
      clearTimeout(t);
      document.removeEventListener('keydown', onKey);
      previousFocus.current?.focus?.();
    };
  }, [open, onClose, dismissable]);

  if (!open) return null;
  const layout = bp === 'mobile' ? 'sheet' : 'dialog';
  const titleId = labelledBy || 'gc-dialog-title';

  return (
    <div
      className={`gc-dialog gc-dialog-${layout} gc-dialog-size-${size}${className ? ` ${className}` : ''}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? titleId : undefined}
    >
      <div
        className="gc-dialog-overlay"
        onClick={dismissable ? onClose : undefined}
        aria-hidden="true"
      />
      <div
        className="gc-dialog-panel"
        ref={containerRef}
        tabIndex={-1}
      >
        {layout === 'sheet' && <div className="gc-dialog-handle" aria-hidden="true" />}
        {(title || dismissable) && (
          <div className="gc-dialog-header">
            {title && <h2 id={titleId} className="gc-dialog-title">{title}</h2>}
            {dismissable && (
              <button
                type="button"
                className="gc-dialog-close"
                aria-label="Close"
                onClick={onClose}
              >
                <LuX aria-hidden="true" />
              </button>
            )}
          </div>
        )}
        {description && <p className="gc-dialog-description">{description}</p>}
        <div className="gc-dialog-body">{children}</div>
        {footer && <div className="gc-dialog-footer">{footer}</div>}
      </div>
    </div>
  );
}

export default ResponsiveDialog;
